import { MfaEnrolment, type PiccoloError, Role, type User } from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  createColumnHelper,
  DataTable,
  Empty,
  type PaginationOptions,
  type RowSelectionOptions,
} from '@piccolohealth/ui';
import React from 'react';
import { TextLink } from '../../components/generic/TextLink';
import { UserAvatar } from '../../components/user/UserAvatar';
import { useAppContext } from '../../hooks/useAppContext';
import { useRole } from '../../hooks/useRole';
import { FeatureFlagTag } from './FeatureFlagTag';
import { RoleTag } from './RoleTag';
import { UserActionMenu } from './UsersActionMenu';

interface Props {
  isLoading: boolean;
  error: PiccoloError | null;
  users: User[];
  refetch: () => Promise<void>;
  pagination?: PaginationOptions;
  rowSelection?: RowSelectionOptions<User>;
}

export const UsersTable = (props: Props) => {
  const { organization } = useAppContext();

  const hasSupportRole = useRole(Role.Support);

  const columns = React.useMemo(() => {
    const columnHelper = createColumnHelper<User>();

    return [
      columnHelper.accessor('name', {
        header: 'Name',
        minSize: 320,
        maxSize: 380,
        cell: (props) => (
          <TextLink to={`/organizations/${organization.id}/users/${props.row.original.id}`}>
            <ChakraV3.HStack gap={4} fontSize='sm'>
              <UserAvatar
                secondary={props.row.original.email}
                picture={props.row.original.picture}
                name={props.row.original.name}
                w='40px'
                h='40px'
                showTooltip={false}
              />
              <ChakraV3.Stack gap={1} lineHeight={1} align='start'>
                <ChakraV3.Text fontWeight='semibold' color='darkPurple' fontSize='md'>
                  {props.row.original.name}
                </ChakraV3.Text>
                <ChakraV3.Text color='secondary'>{props.row.original.email}</ChakraV3.Text>
              </ChakraV3.Stack>
            </ChakraV3.HStack>
          </TextLink>
        ),
      }),
      columnHelper.accessor('title', {
        header: 'Title',
        minSize: 200,
        maxSize: 380,
        cell: (props) => <ChakraV3.Text fontSize='sm'>{props.row.original.title}</ChakraV3.Text>,
      }),
      columnHelper.accessor('practitionerNumber', {
        header: 'Practitioner number',
        minSize: 160,
        maxSize: 160,
        cell: (props) => (
          <ChakraV3.Text fontSize='sm'>
            {props.row.original.practitionerNumber ?? '-'}
          </ChakraV3.Text>
        ),
      }),
      columnHelper.display({
        header: 'MFA Enrolment',
        cell: (ps) => {
          switch (ps.row.original.mfaEnrolment) {
            case MfaEnrolment.Totp:
              return (
                <ChakraV3.Tag colorPalette='blue' size='sm'>
                  Authenticator app
                </ChakraV3.Tag>
              );
            case MfaEnrolment.Phone:
              return (
                <ChakraV3.Tag colorPalette='green' size='sm'>
                  Phone
                </ChakraV3.Tag>
              );
            case null:
              return (
                <ChakraV3.Tag colorPalette='gray' size='sm'>
                  Not enrolled
                </ChakraV3.Tag>
              );
          }
        },
        size: 100,
        minSize: 100,
      }),
      columnHelper.accessor('organizationMemberships.roles', {
        header: 'Roles',
        maxSize: 240,
        minSize: 240,
        cell: (props) => {
          const membership = props.row.original.organizationMemberships.find(
            (organizationMembership) => organizationMembership.organizationId === organization.id,
          );
          const roles = membership?.roles ?? [];

          return (
            <ChakraV3.HStack>
              {roles.map((role) => (
                <RoleTag key={role} role={role} size='sm' />
              ))}
            </ChakraV3.HStack>
          );
        },
      }),
      ...(hasSupportRole
        ? [
            columnHelper.display({
              header: 'Feature flags',
              minSize: 300,
              maxSize: 300,
              cell: (props) => {
                const featureFlags = props.row.original.featureFlags;

                return (
                  <ChakraV3.HStack>
                    {featureFlags.map((flag) => (
                      <FeatureFlagTag key={flag} featureFlag={flag} size='sm' />
                    ))}
                  </ChakraV3.HStack>
                );
              },
            }),
          ]
        : []),

      columnHelper.display({
        header: 'Actions',
        id: 'actions',
        minSize: 140,
        cell: (props) => <UserActionMenu user={props.row.original} size='sm' />,
      }),
    ];
  }, [organization.id, hasSupportRole]);

  return (
    <DataTable
      columns={columns}
      data={props.users}
      isLoading={props.isLoading}
      error={props.error}
      renderEmpty={() => <Empty title='No users found' />}
      pagination={props.pagination}
      rowSelection={props.rowSelection}
      size='md'
    />
  );
};
